// Inline chevron SVGs to avoid an external dependency
function ChevronLeftIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" {...props}>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" {...props}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function ChevronsLeftIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" {...props}>
      <polyline points="17 18 11 12 17 6" />
      <polyline points="11 18 5 12 11 6" />
    </svg>
  );
}

function ChevronsRightIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" {...props}>
      <polyline points="7 18 13 12 7 6" />
      <polyline points="13 18 19 12 13 6" />
    </svg>
  );
}

export function TablePagination({ activePage, itemsCountPerPage, totalItemsCount, onChange }) {
  const hasTotalCount = typeof totalItemsCount === 'number';
  const totalPages = hasTotalCount ? Math.max(1, Math.ceil(totalItemsCount / itemsCountPerPage)) : null;
  const showPagination = itemsCountPerPage && onChange && (hasTotalCount ? totalItemsCount > 0 : true);

  if (!showPagination) {
    return null;
  }

  // Generate pagination range with ellipses
  const getPaginationRange = () => {
    if (!hasTotalCount || !totalPages) return [];

    const totalPageNumbers = 7; // first, last, current, two siblings, and two ellipses

    // If small number of pages, show all
    if (totalPages <= totalPageNumbers) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const leftSiblingIndex = Math.max(activePage - 1, 2);
    const rightSiblingIndex = Math.min(activePage + 1, totalPages - 1);

    const showLeftEllipsis = leftSiblingIndex > 2;
    const showRightEllipsis = rightSiblingIndex < totalPages - 1;

    const pages = [];

    pages.push(1);

    if (showLeftEllipsis) {
      pages.push('left-ellipsis');
    } else {
      for (let i = 2; i < leftSiblingIndex; i++) pages.push(i);
    }

    for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) pages.push(i);

    if (showRightEllipsis) {
      pages.push('right-ellipsis');
    } else {
      for (let i = rightSiblingIndex + 1; i < totalPages; i++) pages.push(i);
    }

    pages.push(totalPages);

    return pages;
  };

  const pagination = getPaginationRange();

  return (
    <div className="table-pagination">
      <span className="table-pagination-label">
        Page {activePage}{hasTotalCount ? ` of ${totalPages}` : ''}
      </span>

      <div className="table-pagination-actions">
        <button
          type="button"
          onClick={() => onChange(1)}
          disabled={activePage <= 1}
          aria-label="First page"
          className={`page-icon ${activePage <= 1 ? 'disabled' : ''}`}
        >
          <ChevronsLeftIcon />
        </button>

        <button
          type="button"
          onClick={() => onChange(Math.max(1, activePage - 1))}
          disabled={activePage <= 1}
          aria-label="Previous page"
          className={`page-icon ${activePage <= 1 ? 'disabled' : ''}`}
        >
          <ChevronLeftIcon />
        </button>

        <div className="page-numbers" aria-hidden={false}>
          {pagination.map((p, idx) =>
            typeof p === 'number' ? (
              <button
                key={p}
                type="button"
                onClick={() => onChange(p)}
                className={`page-number ${activePage === p ? 'active' : ''}`}
                aria-current={activePage === p ? 'page' : undefined}
              >
                {p}
              </button>
            ) : (
              <span key={`${p}-${idx}`} className="page-ellipsis">&hellip;</span>
            )
          )}
        </div>

        <button
          type="button"
          onClick={() => onChange(Math.min(totalPages, activePage + 1))}
          disabled={hasTotalCount ? activePage >= totalPages : false}
          aria-label="Next page"
          className={`page-icon ${hasTotalCount && activePage >= totalPages ? 'disabled' : ''}`}
        >
          <ChevronRightIcon />
        </button>

        <button
          type="button"
          onClick={() => onChange(totalPages)}
          disabled={hasTotalCount ? activePage >= totalPages : false}
          aria-label="Last page"
          className={`page-icon ${hasTotalCount && activePage >= totalPages ? 'disabled' : ''}`}
        >
          <ChevronsRightIcon />
        </button>
      </div>
    </div>
  );
}
